import { ROUTER_PATH } from "@/src/config/router";
import {
  AlphaMarketTabEnum,
  INVEST_PRIMARY_TAB_ACTIVITY,
  INVEST_PRIMARY_TAB_OPPORTUNITY,
  INVEST_PRIMARY_TAB_PORTFOLIO,
  type InvestPrimaryTabId,
} from "@/src/store";
import { buildInvestPortfolioRootPath } from "@/src/utils/investPortfolioRouting";
import { isInvestPortfolioHomePath } from "@/src/utils/portfolioRouting";

export const INVEST_PRIMARY_TAB_SEGMENT = {
  ACTIVITY: "activity",
  OPPORTUNITY: "opportunity",
} as const;

export type InvestPrimaryTabSegment =
  (typeof INVEST_PRIMARY_TAB_SEGMENT)[keyof typeof INVEST_PRIMARY_TAB_SEGMENT];

export const INVEST_PRIMARY_TAB_SEGMENTS: readonly InvestPrimaryTabSegment[] = [
  INVEST_PRIMARY_TAB_SEGMENT.ACTIVITY,
  INVEST_PRIMARY_TAB_SEGMENT.OPPORTUNITY,
];

/** @deprecated 旧 H5 二级 market 路由，仅 legacy 重定向与 Alpha 页数据层使用 */
export const INVEST_MARKET_SLUG = {
  STOCK: "stock",
  ETF: "etf",
  CRYPTO: "crypto",
} as const;

/** @deprecated */
export type InvestMarketSlug =
  (typeof INVEST_MARKET_SLUG)[keyof typeof INVEST_MARKET_SLUG];

/** @deprecated */
export const INVEST_MARKET_SLUGS: readonly InvestMarketSlug[] = [
  INVEST_MARKET_SLUG.STOCK,
  INVEST_MARKET_SLUG.ETF,
  INVEST_MARKET_SLUG.CRYPTO,
];

const H5_TAB_PRIORITY: AlphaMarketTabEnum[] = [
  AlphaMarketTabEnum.US,
  AlphaMarketTabEnum.ETF,
  AlphaMarketTabEnum.CRYPTO,
];

const FOLLOW_MARKET_SET = new Set<string>([
  ...H5_TAB_PRIORITY,
  AlphaMarketTabEnum.HK,
]);

export function isInvestPrimaryTabSegment(
  value: string,
): value is InvestPrimaryTabSegment {
  return (INVEST_PRIMARY_TAB_SEGMENTS as readonly string[]).includes(value);
}

/** @deprecated */
export function isInvestMarketSlug(value: string): value is InvestMarketSlug {
  return (INVEST_MARKET_SLUGS as readonly string[]).includes(value);
}

export function primaryTabSegmentFromId(
  tab: InvestPrimaryTabId,
): InvestPrimaryTabSegment | null {
  switch (tab) {
    case INVEST_PRIMARY_TAB_ACTIVITY:
      return INVEST_PRIMARY_TAB_SEGMENT.ACTIVITY;
    case INVEST_PRIMARY_TAB_OPPORTUNITY:
      return INVEST_PRIMARY_TAB_SEGMENT.OPPORTUNITY;
    case INVEST_PRIMARY_TAB_PORTFOLIO:
    default:
      return null;
  }
}

export function primaryTabIdFromSegment(
  segment: InvestPrimaryTabSegment,
): InvestPrimaryTabId {
  switch (segment) {
    case INVEST_PRIMARY_TAB_SEGMENT.OPPORTUNITY:
      return INVEST_PRIMARY_TAB_OPPORTUNITY;
    case INVEST_PRIMARY_TAB_SEGMENT.ACTIVITY:
      // Activity 已从分段移除；解析到 activity 时映射为 Opportunity（配合 soft redirect）
      return INVEST_PRIMARY_TAB_OPPORTUNITY;
    default:
      return INVEST_PRIMARY_TAB_OPPORTUNITY;
  }
}

/** @deprecated */
export function marketSlugFromTab(tab: AlphaMarketTabEnum): InvestMarketSlug {
  switch (tab) {
    case AlphaMarketTabEnum.ETF:
      return INVEST_MARKET_SLUG.ETF;
    case AlphaMarketTabEnum.CRYPTO:
      return INVEST_MARKET_SLUG.CRYPTO;
    case AlphaMarketTabEnum.US:
    case AlphaMarketTabEnum.HK:
    default:
      return INVEST_MARKET_SLUG.STOCK;
  }
}

/** @deprecated */
export function tabFromMarketSlug(slug: InvestMarketSlug): AlphaMarketTabEnum {
  switch (slug) {
    case INVEST_MARKET_SLUG.ETF:
      return AlphaMarketTabEnum.ETF;
    case INVEST_MARKET_SLUG.CRYPTO:
      return AlphaMarketTabEnum.CRYPTO;
    case INVEST_MARKET_SLUG.STOCK:
    default:
      return AlphaMarketTabEnum.US;
  }
}

/** @deprecated legacy `/invest/{stock|etf|crypto}` → 一级 Tab（默认 Opportunity） */
export function primaryTabFromMarketSlug(
  slug: InvestMarketSlug,
): InvestPrimaryTabId {
  switch (slug) {
    case INVEST_MARKET_SLUG.ETF:
    case INVEST_MARKET_SLUG.STOCK:
    case INVEST_MARKET_SLUG.CRYPTO:
    default:
      return INVEST_PRIMARY_TAB_OPPORTUNITY;
  }
}

/** 浏览器 canonical 路径的 locale 前缀（英文省略）。 */
export function investLocalePathPrefix(locale: string): string {
  return locale === "en" ? "" : `/${locale}`;
}

export function buildInvestRootPath(locale: string): string {
  return buildInvestPrimaryTabPathForLocale(
    locale,
    INVEST_PRIMARY_TAB_OPPORTUNITY,
  );
}

export function buildInvestPrimaryTabPath(
  localePathPrefix: string,
  tab: InvestPrimaryTabId,
): string {
  if (tab === INVEST_PRIMARY_TAB_PORTFOLIO) {
    const locale = localePathPrefix.replace(/^\//, "") || "en";
    return buildInvestPortfolioRootPath(locale);
  }
  // ACTIVITY 已非一级 Tab：路径请求落到 Opportunity
  const resolvedTab =
    tab === INVEST_PRIMARY_TAB_ACTIVITY ? INVEST_PRIMARY_TAB_OPPORTUNITY : tab;
  const segment = primaryTabSegmentFromId(resolvedTab);
  if (!segment) {
    return buildInvestPrimaryTabPath(
      localePathPrefix,
      INVEST_PRIMARY_TAB_OPPORTUNITY,
    );
  }
  const prefix = (localePathPrefix ?? "").replace(/\/+$/, "");
  const path = `${ROUTER_PATH.INVEST}/${segment}`;
  return prefix ? `${prefix}${path}` : path;
}

export function buildInvestPrimaryTabPathForLocale(
  locale: string,
  tab: InvestPrimaryTabId,
): string {
  return buildInvestPrimaryTabPath(investLocalePathPrefix(locale), tab);
}

/** 底部 Shell Invest Tab 链接：按上次一级 Tab 定位到 opportunity / portfolio */
export function buildInvestShellTabHref(
  locale: string,
  activeTab?: InvestPrimaryTabId,
): string {
  if (!activeTab) {
    return buildInvestRootPath(locale);
  }
  const resolved =
    activeTab === INVEST_PRIMARY_TAB_ACTIVITY
      ? INVEST_PRIMARY_TAB_OPPORTUNITY
      : activeTab;
  return buildInvestPrimaryTabPathForLocale(locale, resolved);
}

/** @deprecated */
export function buildInvestMarketPath(
  localePathPrefix: string,
  slug: InvestMarketSlug,
): string {
  const prefix = (localePathPrefix ?? "").replace(/\/+$/, "");
  const path = `${ROUTER_PATH.INVEST}/${slug}`;
  return prefix ? `${prefix}${path}` : path;
}

/** @deprecated */
export function buildInvestMarketPathForLocale(
  locale: string,
  slug: InvestMarketSlug,
): string {
  return buildInvestMarketPath(investLocalePathPrefix(locale), slug);
}

/** @deprecated legacy market segment */
export function buildLegacyMarketRedirectPathForLocale(
  locale: string,
  slug: InvestMarketSlug,
): string {
  return buildInvestPrimaryTabPathForLocale(
    locale,
    primaryTabFromMarketSlug(slug),
  );
}

/** 从 pathname 解析一级 Tab segment（activity / opportunity）。 */
export function parseInvestPrimaryTabFromPathname(
  pathname: string,
): InvestPrimaryTabId | null {
  const segments = (pathname || "").split("/").filter(Boolean);
  const investIndex = segments.indexOf("invest");
  if (investIndex < 0) {
    return null;
  }
  const segment = segments[investIndex + 1];
  if (!segment || !isInvestPrimaryTabSegment(segment)) {
    return null;
  }
  return primaryTabIdFromSegment(segment);
}

/** @deprecated */
export function parseInvestMarketSlugFromPathname(
  pathname: string,
): InvestMarketSlug | null {
  const segments = (pathname || "").split("/").filter(Boolean);
  const investIndex = segments.indexOf("invest");
  if (investIndex < 0) {
    return null;
  }
  const segment = segments[investIndex + 1];
  if (!segment || !isInvestMarketSlug(segment)) {
    return null;
  }
  return segment;
}

/** Invest 主壳根路径：`/invest`、`/invest/{activity|opportunity}`、`/invest/portfolio/**` 或 legacy market。 */
export function isInvestMarketHomePath(pathname: string): boolean {
  const p = pathname || "";
  if (p === ROUTER_PATH.INVEST) {
    return true;
  }
  if (isInvestPortfolioHomePath(p)) {
    return true;
  }
  if (parseInvestPrimaryTabFromPathname(p) !== null) {
    return true;
  }
  return parseInvestMarketSlugFromPathname(p) !== null;
}

/**
 * @deprecated H5 Alpha 页数据层：US 或 HK 偏好均落在 Stock；否则按 Stock > ETF > Crypto。
 */
export function pickInitialMarketTab(
  followMarkets: string[] | null | undefined,
): AlphaMarketTabEnum {
  const normalized = (followMarkets ?? [])
    .map((x) => String(x).toUpperCase())
    .filter((x) => FOLLOW_MARKET_SET.has(x));

  if (normalized.length === 0) {
    return AlphaMarketTabEnum.US;
  }
  if (
    normalized.includes(AlphaMarketTabEnum.US) ||
    normalized.includes(AlphaMarketTabEnum.HK)
  ) {
    return AlphaMarketTabEnum.US;
  }
  for (const candidate of H5_TAB_PRIORITY) {
    if (normalized.includes(candidate)) {
      return candidate;
    }
  }
  return AlphaMarketTabEnum.US;
}

/** @deprecated */
export function pickInitialMarketSlug(
  followMarkets: string[] | null | undefined,
): InvestMarketSlug {
  return marketSlugFromTab(pickInitialMarketTab(followMarkets));
}
